/**
 * The Backlinks tab (1.J deliverable 15), a `rail.tabs` registration of order 10: the pages that
 * link here grouped by type, every mention with its sentence, "Used in" and "Variants" on a
 * reference page, and the pages that name this one without a link (Unlinked).
 */
import type { SlotComponentProps } from '@codex/contracts';
import { Button, Icon, Skeleton, SrdBadge, TypeGlyph } from '@codex/ui';
import { useMemo } from 'react';
import { docHref } from '../../editor-services/adapter.js';
import { useOpenDoc } from '../../shell/open-doc.js';
import { useLinkFlags, useLinkWhen } from '../chrono.js';
import { useLinksLive, usePageSeen } from '../live.js';
import { useBacklinks, useLinkTypes } from '../queries.js';
import { selectLink } from '../selection.js';
import { glyphFor } from '../story/glyph.js';
import { ValidityCells } from '../validity/validity-cells.js';
import { hasValidity } from '../validity/validity-model.js';
import { groupRelations, isGmOnly, referenceGroups } from './backlinks-model.js';
import { UnlinkedSection } from './unlinked.js';
import '../links.css';

export const NO_RELATIONS_TEXT = 'No links here yet.';
export const NO_MENTIONS_TEXT = 'No mentions yet.';
export const BACKLINKS_FAILED_TEXT = "Backlinks couldn't be loaded.";

export function BacklinksTab({ docId }: SlotComponentProps) {
  const open = useOpenDoc();
  const backlinks = useBacklinks(docId);
  const types = useLinkTypes();
  const seen = usePageSeen(docId);
  const flags = useLinkFlags();
  const when = useLinkWhen(flags.chrono);
  const data = backlinks.data;
  const shown = useMemo(
    () => [
      ...(data?.relations.map((r) => r.other.id) ?? []),
      ...(data?.mentions.map((m) => m.source.id) ?? []),
    ],
    [data],
  );
  useLinksLive(docId, shown, seen, {
    validity: flags.chrono && (data?.relations.some((r) => hasValidity(r.link)) ?? false),
  });
  const groups = useMemo(
    () => (data ? groupRelations(data.relations, types.data ?? [], docId) : []),
    [data, types.data, docId],
  );

  if (backlinks.isError) {
    return (
      <div className="cx-links-tab">
        <p role="alert">{BACKLINKS_FAILED_TEXT}</p>
        <Button size="sm" onClick={() => void backlinks.refetch()}>
          Try again
        </Button>
      </div>
    );
  }
  if (!data) return <Skeleton lines={3} label="Loading backlinks" />;

  return (
    <div className="cx-links-tab">
      <section aria-labelledby={`bl-rel-${docId}`}>
        <h3 id={`bl-rel-${docId}`} className="cx-links-tab__heading">
          Relations
        </h3>
        {groups.length === 0 ? (
          <p className="cx-links-story__empty">{NO_RELATIONS_TEXT}</p>
        ) : (
          groups.map((group, index) => (
            <section key={group.label} aria-label={group.label}>
              <h4 id={`bl-grp-${docId}-${index}`} className="cx-links-tab__group">
                {group.label}
              </h4>
              <ul className="cx-links-tab__list" aria-labelledby={`bl-grp-${docId}-${index}`}>
                {group.items.map(({ link, other }) => (
                  <li
                    key={link.id}
                    className="cx-links-tab__item"
                    onFocusCapture={() => selectLink(docId, link.id)}
                    onPointerDown={() => selectLink(docId, link.id)}
                  >
                    <TypeGlyph type={glyphFor(other.typeKey)} />
                    <a href={docHref(other.id)} onClick={(e) => open(other.id, e)}>
                      {other.title}
                    </a>
                    {other.library === 'reference' && <SrdBadge />}
                    {link.label && <span className="cx-links-row__beat">{link.label}</span>}
                    {flags.chrono && (
                      <ValidityCells
                        link={link}
                        docId={docId}
                        facts={{ calendar: when.calendar, now: when.now }}
                      />
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </section>
      <section aria-labelledby={`bl-men-${docId}`}>
        <h3 id={`bl-men-${docId}`} className="cx-links-tab__heading">
          Mentions
        </h3>
        {data.mentions.length === 0 ? (
          <p className="cx-links-story__empty">{NO_MENTIONS_TEXT}</p>
        ) : (
          <ul
            className="cx-links-tab__list"
            data-testid="links.backlinks.list"
            aria-labelledby={`bl-men-${docId}`}
          >
            {data.mentions.map((mention) => (
              <li key={`${mention.source.id}:${mention.blockId}:${mention.context}`}>
                <div className="cx-links-tab__item">
                  <TypeGlyph type={glyphFor(mention.source.typeKey)} />
                  <a href={docHref(mention.source.id, mention.blockId)}>{mention.source.title}</a>
                  {isGmOnly(mention) && (
                    <span className="cx-links-tab__tag">
                      <Icon name="eye-off" size={14} />
                      GM only
                    </span>
                  )}
                </div>
                {mention.context && <p className="cx-links-tab__context">{mention.context}</p>}
              </li>
            ))}
          </ul>
        )}
      </section>
      {referenceGroups(data).map((group, index) => (
        <section key={group.label} aria-label={group.label}>
          <h3 id={`bl-ref-${docId}-${index}`} className="cx-links-tab__heading">
            {group.label}
          </h3>
          <ul className="cx-links-tab__list" aria-labelledby={`bl-ref-${docId}-${index}`}>
            {group.pages.map((page) => (
              <li key={page.id} className="cx-links-tab__item">
                <TypeGlyph type={glyphFor(page.typeKey)} />
                <a href={docHref(page.id)} onClick={(e) => open(page.id, e)}>
                  {page.title}
                </a>
              </li>
            ))}
          </ul>
        </section>
      ))}
      <UnlinkedSection docId={docId} />
    </div>
  );
}
